import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Linking } from 'react-native';

import { ChatImageManager, type CompressedImage, type PickedImage } from '@/lib/chat/ChatImageManager';

export type AttachmentStatus = 'idle' | 'picking' | 'compressing' | 'ready';

export interface AttachmentState {
  originalUri: string | null;
  compressedUri: string | null;
  displayDataUrl: string | null;
  status: AttachmentStatus;
}

export interface UseChatAttachmentResult extends AttachmentState {
  attachFromCamera: () => Promise<void>;
  attachFromLibrary: () => Promise<void>;
  clear: () => Promise<void>;
  /** Reads the compressed file and returns a data URL for sending. Does NOT purge. */
  buildSendDataUrl: () => Promise<string | null>;
  /** Call after a successful send to free cache files. */
  onSendSucceeded: () => Promise<void>;
}

const INITIAL: AttachmentState = {
  originalUri: null,
  compressedUri: null,
  displayDataUrl: null,
  status: 'idle',
};

export function useChatAttachment(): UseChatAttachmentResult {
  const [state, setState] = useState<AttachmentState>(INITIAL);
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  const purgeCurrent = useCallback(async () => {
    const s = stateRef.current;
    await ChatImageManager.purge(s.originalUri);
    await ChatImageManager.purge(s.compressedUri);
  }, []);

  const clear = useCallback(async () => {
    await purgeCurrent();
    setState(INITIAL);
  }, [purgeCurrent]);

  useEffect(() => {
    return () => {
      // Purge on unmount
      const s = stateRef.current;
      ChatImageManager.purge(s.originalUri);
      ChatImageManager.purge(s.compressedUri);
    };
  }, []);

  const handlePicked = useCallback(async (picked: PickedImage) => {
    setState({ ...INITIAL, originalUri: picked.uri, status: 'compressing' });
    try {
      const compressed: CompressedImage = await ChatImageManager.compressForUpload(picked);
      setState({
        originalUri: picked.uri,
        compressedUri: compressed.uri,
        displayDataUrl: compressed.thumbnailDataUrl,
        status: 'ready',
      });
    } catch (err) {
      console.error('[useChatAttachment:compress]', err);
      await ChatImageManager.purge(picked.uri);
      setState(INITIAL);
      Alert.alert('Could not prepare photo', 'Try picking it again.');
    }
  }, []);

  const warnDenied = useCallback((source: 'camera' | 'library') => {
    Alert.alert(
      source === 'camera' ? 'Camera access needed' : 'Photo access needed',
      'Enable access in Settings to attach a photo.',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Open Settings', onPress: () => Linking.openSettings() },
      ],
    );
  }, []);

  const attachFromCamera = useCallback(async () => {
    if (stateRef.current.status !== 'idle') return;
    setState((s) => ({ ...s, status: 'picking' }));
    const ok = await ChatImageManager.requestCameraPermission();
    if (!ok) { setState(INITIAL); warnDenied('camera'); return; }
    const picked = await ChatImageManager.pickFromCamera();
    if (!picked) { setState(INITIAL); return; }
    await handlePicked(picked);
  }, [handlePicked, warnDenied]);

  const attachFromLibrary = useCallback(async () => {
    if (stateRef.current.status !== 'idle') return;
    setState((s) => ({ ...s, status: 'picking' }));
    const ok = await ChatImageManager.requestLibraryPermission();
    if (!ok) { setState(INITIAL); warnDenied('library'); return; }
    const picked = await ChatImageManager.pickFromLibrary();
    if (!picked) { setState(INITIAL); return; }
    await handlePicked(picked);
  }, [handlePicked, warnDenied]);

  const buildSendDataUrl = useCallback(async () => {
    const s = stateRef.current;
    if (!s.compressedUri || s.status !== 'ready') return null;
    return ChatImageManager.toDataUrl(s.compressedUri);
  }, []);

  const onSendSucceeded = useCallback(async () => {
    await purgeCurrent();
    setState((s) => ({ ...s, originalUri: null, compressedUri: null, status: 'idle' }));
    // Note: displayDataUrl is intentionally left untouched — bubble copy is rendered via useChat's message list.
  }, [purgeCurrent]);

  return {
    ...state,
    attachFromCamera,
    attachFromLibrary,
    clear,
    buildSendDataUrl,
    onSendSucceeded,
  };
}
